import { useState } from "react";
import { KeyRound, ShieldOff } from "lucide-react";
import { trpc } from "../../hooks/trpc";

// =====================================================================
// Admin-Panel · Dienste (Migration 0055, nur GF).
//
// Dienstidentitaet "nexus": Nexus fragt andere Hub-Apps nur lesend ab
// (Anspruch nexus:read, docs/nexus-lage.md). Hier wird der langlebige
// Token ausgestellt (einmalige Anzeige, nie gespeichert) und widerrufen.
// Der Server erzwingt GF (serviceIdentity-Router, gfProcedure); diese
// Ansicht ist nur Bedienung.
// =====================================================================

function fmt(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

export function ServiceIdentityView() {
  const utils = trpc.useUtils();
  const statusQuery = trpc.serviceIdentity.status.useQuery();
  const [token, setToken] = useState<{ token: string; expiresAt: Date; tokenVersion: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const issue = trpc.serviceIdentity.issueNexusToken.useMutation({
    onSuccess: async (issued) => {
      setToken({ token: issued.token, expiresAt: new Date(issued.expiresAt), tokenVersion: issued.tokenVersion });
      setCopied(false);
      setError(null);
      await utils.serviceIdentity.status.invalidate();
    },
    onError: (err) => setError(err.message),
  });
  const revoke = trpc.serviceIdentity.revokeNexusToken.useMutation({
    onSuccess: async () => {
      setToken(null);
      setError(null);
      await utils.serviceIdentity.status.invalidate();
    },
    onError: (err) => setError(err.message),
  });

  if (statusQuery.isLoading) {
    return <div className="cc-card"><div className="cc-card-body">Lade Dienstidentitaeten…</div></div>;
  }
  if (statusQuery.error || !statusQuery.data) {
    return (
      <div className="cc-card" data-testid="admin-dienste-error">
        <div className="cc-card-body">
          <span className="cc-badge-st is-bad">Fehler</span>
          <span style={{ marginLeft: 8 }}>{statusQuery.error?.message ?? "Keine Daten"}</span>
        </div>
      </div>
    );
  }

  const s = statusQuery.data;
  const aktiv = Boolean(s.lastIssuedAt) && !s.revokedAt;

  const copy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="admin-dienste">
      <div className="cc-card">
        <div className="cc-card-head">
          <span className="cc-card-title">{s.label}</span>
          <span className={`cc-badge-st ${aktiv ? "is-ok" : "is-warn"}`} data-testid="admin-dienste-status">
            {aktiv ? "Token ausgestellt" : s.revokedAt ? "widerrufen" : "kein Token"}
          </span>
        </div>
        <div className="cc-card-body" style={{ display: "grid", gap: 8 }}>
          <div><span className="cc-row-label">Schluessel</span> <code>{s.key}</code> · <span className="cc-row-label">Anspruch</span> <code>{s.scope}</code> · <span className="cc-row-label">Version</span> {s.tokenVersion}</div>
          <div><span className="cc-row-label">Zuletzt ausgestellt</span> {fmt(s.lastIssuedAt)}{s.lastIssuedExpiresAt ? ` (gueltig bis ${fmt(s.lastIssuedExpiresAt)})` : ""}</div>
          <div><span className="cc-row-label">Zuletzt widerrufen</span> {fmt(s.revokedAt)}</div>
          <p className="cc-sub" style={{ margin: 0 }}>
            Der Token wird nur einmal angezeigt. Ablage als <code>{s.envVar}</code> im Railway Dienst Nexus.
            Widerruf erhoeht die Version: der Hub weist aeltere Tokens innerhalb von 60 Sekunden ab,
            Apps pruefen offline und ziehen den Widerruf alle 5 Minuten ueber <code>GET /api/nexus/token-status</code> nach.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="cc-btn is-primary"
              disabled={issue.isPending}
              onClick={() => {
                if (aktiv && !confirm("Es gibt bereits einen ausgestellten Token. Ein neuer Token macht den alten nicht ungueltig (dafuer Widerruf). Fortfahren?")) return;
                issue.mutate({ ttlDays: s.defaultDays });
              }}
              data-testid="admin-dienste-ausstellen"
            >
              <KeyRound size={14} strokeWidth={1.6} /> Token ausstellen ({s.defaultDays} Tage)
            </button>
            <button
              type="button"
              className="cc-btn"
              disabled={revoke.isPending}
              onClick={() => {
                if (!confirm("Alle bisher ausgestellten Nexus Tokens widerrufen? Nexus verliert sofort den Lesezugriff auf den Hub.")) return;
                revoke.mutate();
              }}
              data-testid="admin-dienste-widerrufen"
            >
              <ShieldOff size={14} strokeWidth={1.6} /> Widerrufen
            </button>
          </div>
          {error ? <div className="cc-badge-st is-bad" data-testid="admin-dienste-fehler">{error}</div> : null}
        </div>
      </div>

      {token ? (
        <div className="cc-card" data-testid="admin-dienste-token">
          <div className="cc-card-head">
            <span className="cc-card-title">Neuer Token (Version {token.tokenVersion}, gueltig bis {fmt(token.expiresAt)})</span>
            <button type="button" className="cc-btn" onClick={copy}>{copied ? "Kopiert" : "Kopieren"}</button>
          </div>
          <div className="cc-card-body">
            <textarea
              readOnly
              value={token.token}
              rows={4}
              style={{ width: "100%", fontFamily: "var(--font-mono, monospace)", fontSize: 12 }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <p className="cc-sub" style={{ margin: "8px 0 0" }}>
              Jetzt als <code>{s.envVar}</code> im Railway Dienst Nexus hinterlegen. Nach dem Verlassen dieser Seite ist der Wert nicht mehr abrufbar.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
